/**
 * La **rente evergreen**, projetée pour l'affichage — SPEC-MOTEUR §6.3.
 *
 * C'est le premier revenu passif du jeu : ce que la bibliothèque rapporte pendant que le
 * joueur dort. ⟦A-9 arc 2⟧ le range parmi les mécaniques du « métier » à enseigner entre 100
 * et 1 000 abonnés — et une mécanique qui ne se voit pas ne s'apprend pas.
 *
 * **Pourquoi ce fichier existe plutôt qu'un appel au moteur.** `verserRente` (le seul endroit
 * où la règle vit) **mute** l'état et les `VideoRecord` : il verse. On ne peut donc pas s'en
 * servir pour une prévision. Ce module en est le **miroir en lecture**, ligne à ligne, et il
 * n'écrit **aucun chiffre en dur** : chaque facteur vient d'une fonction ou d'une constante
 * exportée par `@maxime/shared` (`evergreensActives` pour le tri et le rang, `renteHebdoVues`
 * pour `vues_pic × weekly_rate × decay`, `saturationRente` pour les rendements décroissants,
 * `RENTE_HEBDO_PLAFOND`, `RENTE_MULT_PAUSE`, `RPM_*`).
 *
 * Si la règle bouge côté moteur, elle bouge ici sans qu'on y touche — sauf pour la
 * composition elle-même, qui est la copie assumée du pseudocode §6.3.
 */

import {
  CAPACITE_LORE_DE_CHAINE,
  enPauseAnnoncee,
  estMonetise,
  evergreensActives,
  RENTE_HEBDO_PLAFOND,
  RENTE_MULT_PAUSE,
  RPM_LONGUE,
  RPM_SHORT,
  renteHebdoVues,
  saturationRente,
  type ActiveModifiers,
  type GameState,
  type VideoRecord,
} from "@maxime/shared";

/** Ce qu'une evergreen rapporte par **semaine simulée** (112 h éveillées, §2.1). */
export interface RenteVideo {
  video: VideoRecord;
  /** Rang dans le classement §6.3 — au-delà de 8, la saturation mord. */
  rang: number;
  vues: number;
  abonnes: number;
  /** ⟦A-1⟧ 0 tant que la chaîne n'est pas monétisée : la couche argent n'existe pas encore. */
  argent: number;
  /** `< 1` quand la saturation retient la vidéo (rang > 8) — c'est ce qu'on montre au joueur. */
  saturation: number;
}

export interface RenteChaine {
  lignes: RenteVideo[];
  vues: number;
  abonnes: number;
  argent: number;
  monetise: boolean;
  /** ⟦A-2⟧ La rente tombe à 80 % pendant une pause annoncée — sauf avec *Lore de chaîne*. */
  facteurPause: number;
  /** Nombre d'evergreens au-delà duquel la saturation commence à mordre (`RENTE_SATURATION_RANG`). */
  saturees: number;
}

/**
 * Miroir en lecture de `verserRente` pour un `Δh` d'une semaine pleine (prorata = 1).
 *
 * Les deux subtilités que le moteur documente et qu'on reproduit telles quelles :
 * - le `×1,4` du SEO est **déjà figé** dans `weekly_rate` à la publication ; on n'applique
 *   donc que `multiplier_etat.rente`, sans quoi on le compterait deux fois ;
 * - le plafond de 5 % du pic borne le taux **effectif**, après multiplicateur d'état.
 */
export function renteHebdomadaire(state: GameState, mods: ActiveModifiers): RenteChaine {
  const facteurPause =
    enPauseAnnoncee(state) && !mods.unlocked.has(CAPACITE_LORE_DE_CHAINE) ? RENTE_MULT_PAUSE : 1;
  const monetise = estMonetise(state);

  const lignes: RenteVideo[] = evergreensActives(state).map((video, index) => {
    const rang = index + 1;
    const saturation = saturationRente(rang);

    // `renteHebdoVues` porte `vues_pic × weekly_rate × decay`. Le multiplicateur d'état et le
    // plafond s'appliquent au taux : on les traduit en facteur sur ces vues-là.
    const tauxFige = video.evergreen!.weekly_rate;
    const tauxEff = Math.min(RENTE_HEBDO_PLAFOND, tauxFige * mods.multiplier_etat.rente);
    // `tauxFige > 0` par construction (base 2,5 %) ; la garde évite un NaN si un jour il ne l'est plus.
    const correctionEtat = tauxFige > 0 ? tauxEff / tauxFige : 0;
    const vues =
      renteHebdoVues(video, state.clock_h) * correctionEtat * saturation * facteurPause;

    const rpm = video.format === "short" ? RPM_SHORT : RPM_LONGUE;
    return {
      video,
      rang,
      saturation,
      vues,
      abonnes: vues * video.taux_conversion,
      argent: monetise ? (vues * rpm) / 1000 : 0,
    };
  });

  return {
    lignes,
    vues: lignes.reduce((t, l) => t + l.vues, 0),
    abonnes: lignes.reduce((t, l) => t + l.abonnes, 0),
    argent: lignes.reduce((t, l) => t + l.argent, 0),
    monetise,
    facteurPause,
    saturees: lignes.filter((l) => l.saturation < 1).length,
  };
}
